<template>
    <Modal class="membership-renewal-date-modal"
           title="MEMBERSHIP_RENEWAL_DATE.MODAL_TITLE"
           icon="fa-calendar"
           size="lg"
           :ok="ok"
           :cancel="cancel">
        <div class="selectable-btns membership-renewal-date-btns">
            <div v-for="renewalOption in renewalOptions"
                 :key="renewalOption.type"
                 class="btn btn-lg btn-info"
                 :class="{
                     'active': selectedType === renewalOption.type,
                     'disabled': renewalOption.isDisabled
                 }"
                 @click="selectOption(renewalOption, $event)">
                <h4 class="membership-renewal-date-btns__title">
                    {{ l('MEMBERSHIP_RENEWAL_DATE.FROM--' + renewalOption.type.toUpperCase()) }}
                </h4>

                <div v-if="renewalOption.hasDatePicker"
                     class="membership-renewal-date-btns__date">
                    <DtDatePick ref="date-pick"
                                v-model="selectedCustomDate"
                                class="input-group-flex__input dt-date-pick"
                                :is-date-disabled="datePickerDateDisabled"
                                :input-attributes="{
                                    class: 'form-control input-lg text-center',
                                }">
                        <template v-slot:default="{ toggle }">
                            <input class="form-control input-lg membership-renewal-date-btns__date-input"
                                   :value="formattedCustomDate"
                                   :placeholder="customDateInputPlaceholder"
                                   @click="toggle" />
                        </template>
                    </DtDatePick>
                </div>
                <div v-else
                     class="membership-renewal-date-btns__date membership-renewal-date-btns__date--static">
                    {{ dateFilter(renewalOption.date) }}
                </div>

                <p class="membership-renewal-date-btns__description">
                    {{ l('MEMBERSHIP_RENEWAL_DATE.EXPLANATION--' + renewalOption.type) }}
                </p>
            </div>
        </div>
    </Modal>
</template>

<script>
import DateFormat from '@/lib/Dates/DateFormat';
import DtDatePick from '@/vue/components/DatePicker/DtDatePick';
import MembershipRenewalDateTypes from '@/lib/Memberships/MembershipRenewalDateTypes';
import Modal from '@/vue/components/Modals/Modal';
import moment from 'moment';
import { container } from '@/container';
import { dateFilter } from '@/vue/filters/dateFilter';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    components: {
        DtDatePick,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {Membership}
         */
        membership: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            /**
             * @type {Date}
             */
            now: new Date(),

            /**
             * @type {Date}
             */
            dayAfterExpiry: moment(this.membership.currentSubscription.endDate).add(1, 'days').toDate(),

            /**
             * @type {Date}
             */
            minimumDate: moment(this.membership.earliestAllowedRenewalDate).toDate(),

            /**
             * @type {string}
             */
            selectedType: null,

            /**
             * @type {string}
             */
            selectedCustomDate: null,
        };
    },

    computed: {
        /**
         * @return {{type: string, date: Date, isDisabled: boolean, hasDatePicker: boolean}[]}
         */
        renewalOptions() {
            return [
                {
                    type: MembershipRenewalDateTypes.TODAY,
                    date: new Date(),
                    isDisabled: new Date() < this.minimumDate,
                    hasDatePicker: false,
                },
                {
                    type: MembershipRenewalDateTypes.EXPIRY,
                    date: this.membership.earliestAllowedRenewalDate,
                    isDisabled: false,
                    hasDatePicker: false,
                },
                {
                    type: MembershipRenewalDateTypes.OTHER,
                    date: null,
                    isDisabled: false,
                    hasDatePicker: true,
                },
                {
                    type: MembershipRenewalDateTypes.NONE,
                    date: null,
                    isDisabled: false,
                    hasDatePicker: false,
                },
            ];
        },

        /**
         * @return {string}
         */
        customDateInputPlaceholder() {
            return container.langService.getText('MEMBERSHIP_RENEWAL_DATE.OTHER_PLACEHOLDER');
        },

        /**
         * @return {string|null}
         */
        formattedCustomDate() {
            if (this.selectedCustomDate) {
                return moment(this.selectedCustomDate).format(DateFormat.DATE);
            }

            return null;
        },

        /**
         * @return {Date|null}
         */
        selectedDate() {
            switch (this.selectedType) {
                case MembershipRenewalDateTypes.TODAY:
                case MembershipRenewalDateTypes.EXPIRY:
                    return this.renewalOptions
                        .find((opt) => opt.type === this.selectedType)
                        .date;

                case MembershipRenewalDateTypes.OTHER:
                    return this.selectedCustomDate ? moment(this.selectedCustomDate).toDate() : null;

                case MembershipRenewalDateTypes.NONE:
                default:
                    return null;
            }
        },
    },

    created() {
        let initialOption;
        if (this.membership.chosenRenewalOption.type) {
            // If an initial type was provided find the corresponding option and select it.
            initialOption = this.renewalOptions.find(
                (o) => o.type === this.membership.chosenRenewalOption.type
            );
        } else {
            // If no initial type was provided select the default option of "today".
            initialOption = this.renewalOptions.find(
                (o) => o.type === MembershipRenewalDateTypes.TODAY
            );
        }

        if (initialOption) {
            this.selectOption(initialOption);
        }

        // Set the default value for the custom date picker.
        if (this.membership.chosenRenewalOption.chosenDate) {
            this.selectedCustomDate = moment(this.membership.chosenRenewalOption.chosenDate).format('YYYY-MM-DD');
        } else {
            this.selectedCustomDate = moment(this.membership.earliestAllowedRenewalDate).format('YYYY-MM-DD');
        }
    },

    methods: {
        /**
         * Function given to the date picker component to disable dates in the past.
         *
         * @param {Date} date
         *
         * @return {boolean}
         */
        datePickerDateDisabled(date) {
            return date < this.minimumDate;
        },

        /**
         * @param {{}} renewalOption
         * @param {MouseEvent} [event]
         */
        selectOption(renewalOption, event) {
            if (renewalOption.isDisabled) {
                return;
            }

            this.selectedType = renewalOption.type;

            if (event && renewalOption.type === MembershipRenewalDateTypes.OTHER) {
                // If the click happened inside the date picker don't re-open the date picker.
                const datePickElement = document.querySelector('.vdpFloating');
                if (
                    datePickElement
                    && (event.target === datePickElement || datePickElement.contains(event.target))
                ) {
                    return;
                }

                // Open the date picker on click.
                setTimeout(() => {
                    this.$refs['date-pick'][0].open();
                }, 50);
            }
        },

        ok() {
            if (this.selectedType === MembershipRenewalDateTypes.NONE) {
                // Return now if "no renewal" was selected.
                this.resolve({
                    type: this.selectedType,
                    date: null,
                });
                return;
            }

            if (!this.selectedDate) {
                // Ensure a date is entered. Mostly for if "other" is selected, but may also catch some
                // bugs with the default types.
                container.toastFactory.warningTop(
                    null,
                    container.langService.getText('MEMBERSHIP_RENEWAL_DATE.ERROR--NO_DATE'),
                );

                return;
            }

            // It's a real date; make sure it's not too early.
            if (this.selectedDate < this.minimumDate) {
                container.toastFactory.warningTop(
                    null,
                    container.langService.getText(
                        'MEMBERSHIP_RENEWAL_DATE.ERROR--MIN_DATE',
                        {
                            date: dateFilter(this.minimumDate),
                        },
                    ),
                );

                return;
            }

            this.resolve({
                type: this.selectedType,
                date: this.selectedDate,
            });
        },
    },
};
</script>

<style lang="less">
@import "../../../../less/variables";

.membership-renewal-date-btns {
    display: flex;
    grid-gap: @gutter-width;
    flex-direction: column;;

    .btn {
        flex-grow: 1;
        flex-basis: 0;
        min-width: 0;
        margin: 0;
        white-space: normal;
        padding: 15px 10px 10px !important;
        height: auto;
        min-height: unset;
    }

    &__title {
        flex-grow: 0;
        font-weight: normal;
        margin: 0 0 @gutter-width-mobile 0;
    }

    &__date {
        flex-grow: 1;
        margin: @gutter-width-mobile 0;
        font-size: 20px;
        font-weight: bold;

        &--static {
            line-height: 45px;
        }
    }

    &__date-input {
        padding-left: 0 !important;
        padding-right: 0 !important;
        text-align: center;
    }

    &__description {
        flex-grow: 0;
        font-size: @font-size-small;
        line-height: 1.8;
    }
}

@media (min-width: @non-mobile-min) {
    .membership-renewal-date-modal {
        .modl-body {
            // Fix the date picker popup not fitting in the modal.
            // Note that you can't used scoped styles for this to work.
            overflow: visible;
        }
    }

    .membership-renewal-date-btns {
        flex-direction: row;

        .btn {
            display: flex;
            flex-direction: column;
            padding: 17px 10px 12px !important;
        }

        &__title {
            margin: 0 0 @gutter-width 0;
        }

        &__date {
            min-height: 45px;
            margin: @gutter-width 0;
        }

        &__description {
            margin-bottom: @gutter-width;
        }
    }
}
</style>
